import { FC, createContext, useCallback, useMemo, useState } from 'react'
import { createPortal } from 'react-dom'
import { generateUEID } from '../../helper-functions/ueid'
import {
  IContextModel,
  Props,
  ToastContent,
  ToastModel,
  ToastOptions,
  ToastType,
} from './types'
import { Toast } from '~/shared/ui'

const initialState: IContextModel = {
  open: () => {},
}

export const ToastContext = createContext<IContextModel>(initialState)

export const ToastProvider: FC<Props> = ({ children }) => {
  const [toasts, setToasts] = useState<ToastModel[]>([])

  const open = useCallback(
    (content: ToastContent, type: ToastType, options?: ToastOptions) => {
      const { replaceKey, ...toastOptions } = options ?? {}
      const id = generateUEID()
      setToasts((currentToasts) => [
        ...currentToasts.filter(
          (toast) =>
            replaceKey === undefined || toast.replaceKey !== replaceKey,
        ),
        { id, content, type, replaceKey, options: toastOptions },
      ])
    },
    [],
  )

  const close = useCallback(
    (id: string) =>
      setToasts((currentToasts) =>
        currentToasts.filter((toast) => toast.id !== id),
      ),
    [],
  )

  const value = useMemo(() => ({ open }), [open])

  return (
    <ToastContext.Provider value={value}>
      {children}

      {createPortal(
        <div className="fixed top-3 right-3 z-[1100] flex max-h-[calc(100dvh-1.5rem)] max-w-[calc(100vw-1.5rem)] flex-col gap-3 overflow-y-auto overscroll-contain p-1">
          {toasts.map((toast) => (
            <Toast
              key={toast.id}
              variant={toast.type}
              close={() => close(toast.id)}
              {...toast.options}
            >
              {typeof toast.content === 'function'
                ? toast.content(() => close(toast.id))
                : toast.content}
            </Toast>
          ))}
        </div>,
        document.body,
      )}
    </ToastContext.Provider>
  )
}
